import { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import Arrow from './Arrow';
import HeroHeading from './HeroHeading';
import { FieldPanel, FieldPresets } from './FieldControls';
import { TransitionLink } from '../lib/pageTransition';
import { useField } from '../lib/field';
import { projects } from '../data/projects';
import { EMAIL } from './Footer';

const CITY = 'Toronto';
const ZONE = 'America/Toronto';

// The ghosts mark: a strobe ghost with its two coloured afterimages.
const GhostsMark = () => (
  <svg className="hero-credit-mark" width="18" height="18" viewBox="0 0 64 64" aria-hidden="true">
    <g transform="translate(32 33) scale(.8) translate(-32 -32.5)">
      <path d="M8 58V30a24 24 0 0 1 48 0v28l-6-6-6 6-6-6-6 6-6-6-6 6-6-6-6 6z" fill="#5d1119" transform="translate(-5 3)" />
      <path d="M8 58V30a24 24 0 0 1 48 0v28l-6-6-6 6-6-6-6 6-6-6-6 6-6-6-6 6z" fill="#2b606c" transform="translate(5 -2)" />
      <path d="M8 58V30a24 24 0 0 1 48 0v28l-6-6-6 6-6-6-6 6-6-6-6 6-6-6-6 6z" fill="#e4e3e2" />
      <circle cx="19" cy="25" r="4.5" fill="#030406" />
      <circle cx="45" cy="34" r="2.6" fill="#030406" />
    </g>
  </svg>
);

// What I make, one at a time under the name.
const MAKES = ['software', 'visuals', 'automations', 'agents', 'design'];

// "I make software.", the last word swapping every couple of seconds. With
// reduced motion it's the whole list, still.
const Makes = ({ reduce }) => {
  const [at, setAt] = useState(0);
  useEffect(() => {
    if (reduce) return undefined;
    const tick = window.setInterval(() => setAt((i) => (i + 1) % MAKES.length), 2200);
    return () => window.clearInterval(tick);
  }, [reduce]);

  const all = `${MAKES[0][0].toUpperCase()}${MAKES.slice(0, -1).join(', ').slice(1)} and ${MAKES[MAKES.length - 1]}.`;
  if (reduce) return all;
  return (
    <>
      <span className="visually-hidden">I make {MAKES.slice(0, -1).join(', ')} and {MAKES[MAKES.length - 1]}.</span>
      <span className="hero-makes" aria-hidden="true">
        I make{' '}
        <span className="hero-makes-slot">
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.span
              key={MAKES[at]}
              className="hero-makes-word"
              initial={{ y: '0.9em', opacity: 0, filter: 'blur(6px)' }}
              animate={{ y: 0, opacity: 1, filter: 'blur(0px)' }}
              exit={{ y: '-0.9em', opacity: 0, filter: 'blur(6px)' }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
              {MAKES[at]}.
            </motion.span>
          </AnimatePresence>
        </span>
      </span>
    </>
  );
};

const localTime = () =>
  new Intl.DateTimeFormat('en-CA', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: ZONE })
    .format(new Date())
    .replace(/^24/, '00');

// The hero: the name set low and left on the backdrop's field, like a caption
// on a photograph; the field is the picture. The field itself lives behind the
// whole site (Backdrop.js), so the hero is a window onto it.
const IdentityStrip = () => {
  const reduce = useReducedMotion();
  const [time, setTime] = useState(localTime);
  const [touched, setTouched] = useState(false);
  // The arrival: the name word by word, then the field develops behind it,
  // then the rest. If ghosts is slow the rest comes anyway. Coming back to a
  // field that's already lit skips straight to the end.
  const field = useField();
  const [first] = useState(() => !field.lit);
  const [late, setLate] = useState(Boolean(reduce));
  const lit = field.lit || late;
  useEffect(() => {
    const timer = window.setTimeout(() => setLate(true), 3500);
    return () => window.clearTimeout(timer);
  }, []);

  const building = projects.filter((project) => project.status === 'In progress' && project.lead);

  useEffect(() => {
    const tick = window.setInterval(() => setTime(localTime()), 30000);
    return () => window.clearInterval(tick);
  }, []);

  // Held back (`gate` false) until the field has arrived.
  const rise = (delay, gate = true) => ({
    initial: reduce || !first ? false : { opacity: 0, y: 14 },
    animate: gate ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 },
    transition: { duration: 0.9, delay: gate && first ? delay : 0, ease: [0.16, 1, 0.3, 1] },
  });

  return (
    <section
      className={`hero ${reduce ? 'is-still' : ''}`}
      onPointerMove={() => !touched && setTouched(true)}
    >
      <div className="hero-scrim" aria-hidden="true" />

      <div className="hero-body">
        <div className="hero-copy">
          <HeroHeading reduce={Boolean(reduce)} arrive={first && !reduce} />
          <motion.p className="hero-sub" {...rise(0.9, lit)}>
            <Makes reduce={Boolean(reduce)} />
          </motion.p>
          <motion.div className="hero-actions" {...rise(1.05, lit)}>
            <TransitionLink to="/projects" className="hero-cta">
              <span>See the work</span>
              <Arrow />
            </TransitionLink>
            <a className="hero-link" href={`mailto:${EMAIL}?subject=Working%20together`}>
              Work with me
            </a>
          </motion.div>
        </div>

        <motion.div className="hero-foot" {...rise(1.3, lit)}>
          <p className="hero-status">
            <span className="hero-status-time">
              {CITY} {time}
            </span>
            {building.length > 0 && (
              <span className="hero-status-now">
                Building {building.map((project, i) => (
                  <span key={project.slug}>
                    {i > 0 && (i === building.length - 1 ? ' and ' : ', ')}
                    {project.title}
                  </span>
                ))}
              </span>
            )}
          </p>
          <div className="hero-foot-right">
            {!reduce && (
              <span className={`hero-hint ${touched ? 'is-done' : ''}`} aria-hidden="true">
                move to stir the field
              </span>
            )}
            <FieldPresets />
          </div>
        </motion.div>
      </div>

      {/* The credit sits in the corner, apart from the controls. */}
      <motion.div className="hero-credit-corner" {...rise(1.45, lit)}>
        <TransitionLink to="/projects/ghosts-playground" className="hero-credit">
          <span className="hero-credit-title">
            Made with ghosts
            <GhostsMark />
          </span>
        </TransitionLink>
      </motion.div>

      <FieldPanel />
    </section>
  );
};

export default IdentityStrip;
